import { useState, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col } from 'react-bootstrap';

//components
import withLocalization from '~/hoc/withLocalization';
import Button from '~/components/CustomButton/CustomButton.jsx';
import GenericList from '~/components/GenericList/GenericList';
import Checkbox from '~/components/CustomCheckbox/CustomCheckbox';
import { SingleUserGroupsWidget } from '~/components/GenericForm/Widgets/UserGroupWidget';
import { CheckBoxWidget } from '~/components/GenericForm/Widgets/CheckBoxWidget';

//elements
import { Input, Textarea } from '~/elements/Input';
import Select from '~/elements/Select';

const DEFAULT_FORM = { title: '', message: '' };
const DEFAULT_CHANNELS = { push: false, email: false, system: false };
const PAGE_SIZE = 10;
const SOURCE_OPTIONS = [
    { value: 'timelogs', label: 'Timelogs' },
    { value: 'schedule', label: 'Schedule' },
    { value: 'vacations', label: 'Vacations' },
    { value: 'absences', label: 'Absences' },
    { value: 'subscriptions', label: 'Subscriptions' },
    { value: 'document', label: 'Documents' },
    { value: 'checklist', label: 'Checklist' },
    { value: 'other', label: 'Other' },
];

const SendDirectNotification = ({ t, notificationStore, commonStore, userStore }) => {
    const [filters, setFilters] = useState({ name: '', group: '' });
    const [appliedFilters, setAppliedFilters] = useState({ name: '', group: '' });
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [currentPageUserIds, setCurrentPageUserIds] = useState([]);
    const [channels, setChannels] = useState({ ...DEFAULT_CHANNELS });
    const [form, setForm] = useState({ ...DEFAULT_FORM });
    const [source, setSource] = useState('');
    const [sending, setSending] = useState(false);

    const selectedUserIdsRef = useRef(selectedUserIds);
    selectedUserIdsRef.current = selectedUserIds;

    const fetchUsers = async ({ page = 0, pagesize = PAGE_SIZE, filters: reqFilters } = {}) => {
        const finalFilters = { ...(reqFilters || {}) };
        const currentUser = userStore.currentUser;
        if (currentUser && currentUser.user_type === 'pm' && currentUser.Manages && currentUser.Manages.length) {
            finalFilters.pmMembers = currentUser.Manages.join(',');
        }
        const users = await notificationStore.loadNotificationUsers({ filters: finalFilters, page, pagesize });
        setCurrentPageUserIds((users.rows || []).map(u => u.id));
        return users;
    };

    const handleGroupChange = (val) => {
        const updatedFilters = { ...filters, group: val || '' };
        setFilters(updatedFilters);
        setAppliedFilters(updatedFilters);
    };

    const toggleUser = (id) => {
        setSelectedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const isAllOnPageSelected = currentPageUserIds.length > 0 && currentPageUserIds.every(id => selectedUserIds.includes(id));

    const toggleSelectAll = () => {
        setSelectedUserIds(prev => {
            if (isAllOnPageSelected) {
                return prev.filter(id => !currentPageUserIds.includes(id));
            }
            const idsToAdd = currentPageUserIds.filter(id => !prev.includes(id));
            return [...prev, ...idsToAdd];
        });
    };

    const handleFormChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const activeChannels = Object.keys(channels).filter(channel => channels[channel]);
    const canSend = selectedUserIds.length > 0 && activeChannels.length > 0 && form.message.trim() && form.title.trim();

    const handleSend = async () => {
        if (!canSend) return;
        setSending(true);
        try {
            const res = await notificationStore.sendImmediateNotificationToUser({
                user_ids: selectedUserIds,
                channels: activeChannels,
                title: form.title.trim(),
                message: form.message.trim(),
                source,
            });
            commonStore.addNotification(`${t('Notification queued for')} ${res.queued} ${t('user(s)')}`, null, 'success');
            setSelectedUserIds([]);
            setForm({ ...DEFAULT_FORM });
            setSource('');
        } catch (e) {
            commonStore.addNotification(e && e.message ? e.message : t('Something went wrong'), null, 'error');
        } finally {
            setSending(false);
        }
    };

    const renderUserListHeader = () => (
        <Row className="g-2 align-items-center">
            <Col md={3}>
                <div className="d-flex align-items-center gap-1">
                    <Checkbox
                        inlineBlock
                        isChecked={isAllOnPageSelected}
                        onChange={toggleSelectAll}
                        wrapperClassName="checkbox-position-fix"
                        labelClassName="ps-0"
                    />
                    <small className="text-muted">{t('Select All')}</small>
                </div>
            </Col>

            <Col md={4}>
                <SingleUserGroupsWidget
                    value={filters.group}
                    onChange={handleGroupChange}
                />
            </Col>

            <Col md={5}>
                <div className="d-flex conversation-search">
                    <input
                        type="text"
                        className="conversation-search-input"
                        placeholder={`${t('Search')} ${t('Employee')}`}
                        value={filters.name}
                        onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') setAppliedFilters(filters);
                        }}
                    />
                    <i
                        className="conversation-search-icon fa fa-search cursor-pointer"
                        onClick={() => setAppliedFilters(filters)}
                    />
                </div>
            </Col>
        </Row>
    );

    const columns = [
        {
            Header: t('Select'),
            id: 'select',
            width: 90,
            sortable: false,
            textAlign: 'center',
            Cell: ({ row }) => (
                <Checkbox
                    inlineBlock
                    isChecked={selectedUserIdsRef.current.includes(row.original.id)}
                    onChange={() => toggleUser(row.original.id)}
                    wrapperClassName="checkbox-position-fix"
                    labelClassName="ps-0"
                />
            ),
        },
        {
            Header: t('Name'),
            id: 'name',
            textAlign: 'start',
            Cell: ({ row }) => `${row.original.first_name || ''} ${row.original.last_name || ''}`.trim(),
        },
    ];

    return (
        <div className="mt-2">
            <Row className="g-3">
                <Col md={6}>
                    <label className="control-label">{t('Select Employees')}</label>
                    <GenericList
                        columns={columns}
                        requestData={fetchUsers}
                        filters={appliedFilters}
                        pageSize={PAGE_SIZE}
                        header={renderUserListHeader()}
                    />
                </Col>

                <Col md={6}>
                    <label className="control-label">{t('Channels')}</label>
                    <div className="d-flex gap-4 mt-1 mb-3">
                        <CheckBoxWidget
                            value={channels.push}
                            onChange={(checked) => setChannels(prev => ({ ...prev, push: checked, system: checked ? true : prev.system }))}
                            label={t('Push')}
                        />
                        <CheckBoxWidget
                            value={channels.email}
                            onChange={(checked) => setChannels(prev => ({ ...prev, email: checked }))}
                            label={t('Email')}
                        />
                        <CheckBoxWidget
                            value={channels.system}
                            onChange={(checked) => setChannels(prev => ({ ...prev, system: checked, push: checked ? prev.push : false }))}
                            label={t('System')}
                        />
                    </div>

                    <label className="control-label">{t('Source')}</label>
                    <Select
                        options={SOURCE_OPTIONS.map(o => ({ ...o, label: t(o.label) }))}
                        value={source ? { value: source, label: t(SOURCE_OPTIONS.find(o => o.value === source)?.label) } : null}
                        onChange={(opt) => setSource(opt ? opt.value : '')}
                        isClearable
                        className="mt-1"
                    />

                    <div className="mt-3">
                        <Input
                            label={t('Title')}
                            value={form.title}
                            placeholder={t('e.g. Shift reminder')}
                            onChange={e => handleFormChange('title', e.target.value)}
                        />
                    </div>

                    <div className="mt-3">
                        <Textarea
                            label={t('Message')}
                            value={form.message}
                            placeholder={t('Type a message')}
                            onChange={e => handleFormChange('message', e.target.value)}
                        />
                    </div>

                    <div className="mt-3 d-flex justify-content-end">
                        <Button dynamic_lg fill onClick={handleSend} disabled={!canSend || sending}>
                            <i className="fa fa-paper-plane me-2" />
                            {sending ? t('Sending') : `${t('Send Now')}${selectedUserIds.length > 0 ? ` (${selectedUserIds.length})` : ''}`}
                        </Button>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default inject('notificationStore', 'commonStore', 'userStore')(withLocalization(observer(SendDirectNotification)));
